import React, { useState, useEffect } from 'react';
import accessLogsService from '../../../services/accessLogsService';
import './ExportReportsTab.css';

const ExportReportsTab = ({ darkMode }) => {
  const [reportType, setReportType] = useState('compliance');
  const [format, setFormat] = useState('CSV');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [exports, setExports] = useState([]);
  const [exportsLoading, setExportsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  const reportTypes = [
    { value: 'compliance', label: 'Compliance Report' },
    { value: 'security_audit', label: 'Security Audit' },
    { value: 'user_activity', label: 'User Activity Report' },
    { value: 'team_activity', label: 'Team Activity Report' },
    { value: 'time_summary', label: 'Time-based Activity Summary' }
  ];

  const formats = ['CSV', 'JSON', 'PDF'];

  useEffect(() => {
    loadExports();
    // Refresh exports every 5 seconds if there are any pending/processing exports
    const interval = setInterval(() => {
      if (exports.some(e => e.status === 'pending' || e.status === 'processing')) {
        loadExports();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [page]);

  const loadExports = async () => {
    try {
      setExportsLoading(true);
      const result = await accessLogsService.getExports(page, 20);
      setExports(result.exports || []);
      setTotalPages(result.totalPages || 1);
    } catch (err) {
      console.error('Failed to load exports:', err);
    } finally {
      setExportsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const request = {
        reportType,
        format,
        filters: {
          startDate: dateRange.start ? new Date(dateRange.start).toISOString() : null,
          endDate: dateRange.end ? new Date(dateRange.end).toISOString() : null
        }
      };

      const result = await accessLogsService.createExport(request);
      setSuccess(`Export created successfully! ${result.status === 'completed' ? 'Ready to download.' : 'Processing...'}`);
      
      // Refresh exports list
      await loadExports();
    } catch (err) {
      setError(err.message || 'Failed to create export');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (exportId, fileName) => {
    try {
      setDownloadingId(exportId);
      await accessLogsService.downloadExport(exportId);
    } catch (err) {
      alert(err.message || 'Failed to download export');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePrint = async (exportId) => {
    try {
      setPrintingId(exportId);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = `/api/admin/logs/export/${exportId}/download`;
      
      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to print reports');
        return;
      }

      // Fetch the file and display it
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load export for printing');
      }

      const contentType = response.headers.get('Content-Type') || '';
      
      if (contentType.includes('html') || contentType.includes('pdf')) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        printWindow.location.href = blobUrl;
        
        // Wait for content to load, then print
        setTimeout(() => {
          printWindow.print();
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      } else {
        // For CSV/JSON, convert to text and display in printable format
        const text = await response.text();
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Export Report</title>
            <style>
              body { font-family: monospace; padding: 20px; }
              pre { white-space: pre-wrap; word-wrap: break-word; }
            </style>
          </head>
          <body>
            <pre>${text}</pre>
          </body>
          </html>
        `);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 500);
      }
    } catch (err) {
      alert(err.message || 'Failed to print export');
    } finally {
      setPrintingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = `status-badge status-${status.toLowerCase()}`;
    const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
    return <span className={statusClass}>{statusLabel}</span>;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className={`access-logs-tab-content export-reports-tab ${darkMode ? 'dark-mode' : ''}`}>
      <div className="export-section">
        <h3>Generate New Report</h3>
        <div className="export-form">
          <div className="form-row">
            <div className="form-group">
              <label>Report Type</label>
              <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Format</label>
              <select value={format} onChange={(e) => setFormat(e.target.value)}>
                {formats.map(fmt => (
                  <option key={fmt} value={fmt}>{fmt}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
          </div>
          <button 
            className="btn-generate" 
            onClick={handleExport} 
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </div>
      </div>

      <div className="export-history-section">
        <h3>Export History</h3>
        {exportsLoading ? (
          <div className="loading">Loading exports...</div>
        ) : exports.length === 0 ? (
          <div className="empty-state">No exports yet. Generate a report to get started.</div>
        ) : (
          <>
            <div className="exports-table">
              <table>
                <thead>
                  <tr>
                    <th>Report Type</th>
                    <th>Format</th>
                    <th>Status</th>
                    <th>File Size</th>
                    <th>Created</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exports.map(exp => (
                    <tr key={exp.id}>
                      <td>{reportTypes.find(t => t.value === exp.reportType)?.label || exp.reportType}</td>
                      <td>{exp.format}</td>
                      <td>{getStatusBadge(exp.status)}</td>
                      <td>{formatFileSize(exp.fileSize)}</td>
                      <td>{formatDate(exp.createdAt)}</td>
                      <td>{formatDate(exp.completedAt)}</td>
                      <td className="actions-cell">
                        {exp.status === 'completed' && (
                          <>
                            <button
                              className="btn-download"
                              onClick={() => handleDownload(exp.id, exp.fileName)}
                              disabled={downloadingId === exp.id}
                              title="Download"
                            >
                              {downloadingId === exp.id ? 'Downloading...' : '⬇ Download'}
                            </button>
                            {(exp.format === 'PDF' || exp.format === 'HTML') && (
                              <button
                                className="btn-print"
                                onClick={() => handlePrint(exp.id)}
                                disabled={printingId === exp.id}
                                title="Print"
                              >
                                {printingId === exp.id ? 'Opening...' : '🖨 Print'}
                              </button>
                            )}
                          </>
                        )}
                        {exp.status === 'failed' && exp.errorMessage && (
                          <span className="error-text" title={exp.errorMessage}>Error</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))} 
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span>Page {page} of {totalPages}</span>
                <button 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExportReportsTab;
